import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import BuildIcon from '@mui/icons-material/Build';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { TreatmentType } from '../../types/treatment.types';

interface CaseTreatmentsSummaryProps {
  isCollapsed?: boolean;
  isActive?: boolean;
}

export const CaseTreatmentsSummary: React.FC<CaseTreatmentsSummaryProps> = ({
  isCollapsed = false,
  isActive = false,
}) => {
  const { currentCase } = useCases();

  // Get selected treatments from case
  const selectedTreatments = useMemo(() => {
    return currentCase?.selected_treatments || [];
  }, [currentCase?.selected_treatments]);

  // Count treatments by type
  const treatmentCounts = useMemo(() => {
    const counts = {
      drugs: 0,
      procedures: 0,
      other: 0,
    };
    selectedTreatments.forEach((t) => {
      if (t.type === TreatmentType.Drug) counts.drugs++;
      else if (t.type === TreatmentType.Procedure) counts.procedures++;
      else counts.other++;
    });
    return counts;
  }, [selectedTreatments]);

  const backgroundColor = isActive ? 'rgba(0, 150, 136, 0.12)' : 'transparent';
  const totalCount = selectedTreatments.length;

  // Collapsed view - just show icon with count
  if (isCollapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1.5,
          backgroundColor,
          borderLeft: isActive ? '3px solid #009688' : 'none',
        }}
      >
        <Tooltip
          title={
            totalCount > 0
              ? `${totalCount} treatment${totalCount !== 1 ? 's' : ''} selected`
              : 'No treatments selected'
          }
        >
          <IconButton size="small" color={isActive ? 'primary' : 'default'}>
            <MedicationIcon />
          </IconButton>
        </Tooltip>
        {totalCount > 0 && (
          <Chip
            label={totalCount}
            size="small"
            color="primary"
            sx={{ mt: 0.5, height: 18, fontSize: '0.7rem' }}
          />
        )}
      </Box>
    );
  }

  // Expanded view
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor,
        borderLeft: isActive ? '3px solid #009688' : 'none',
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <MedicationIcon
          color={isActive ? 'primary' : 'inherit'}
          sx={{ mr: 1 }}
        />
        <Typography
          variant="subtitle1"
          color={isActive ? 'primary' : 'inherit'}
          sx={{ flexGrow: 1, textAlign: 'left' }}
        >
          Treatments
        </Typography>
        {totalCount > 0 && (
          <Chip label={totalCount} size="small" color="primary" />
        )}
      </Box>

      {totalCount === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'left' }}
        >
          No treatments selected
        </Typography>
      ) : (
        <Box>
          {/* Treatment type breakdown */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {treatmentCounts.drugs > 0 && (
              <Chip
                icon={<MedicationIcon sx={{ fontSize: 16 }} />}
                label={`${treatmentCounts.drugs} medication${
                  treatmentCounts.drugs !== 1 ? 's' : ''
                }`}
                size="small"
                variant="outlined"
                color="info"
              />
            )}
            {treatmentCounts.procedures > 0 && (
              <Chip
                icon={<MedicalServicesIcon sx={{ fontSize: 16 }} />}
                label={`${treatmentCounts.procedures} procedure${
                  treatmentCounts.procedures !== 1 ? 's' : ''
                }`}
                size="small"
                variant="outlined"
                color="warning"
              />
            )}
            {treatmentCounts.other > 0 && (
              <Chip
                icon={<BuildIcon sx={{ fontSize: 16 }} />}
                label={`${treatmentCounts.other} other`}
                size="small"
                variant="outlined"
                color="secondary"
              />
            )}
          </Box>

          {/* Treatment names (show first few) */}
          <Box sx={{ mt: 1, textAlign: 'left' }}>
            {selectedTreatments.slice(0, 3).map((treatment, idx) => (
              <Typography
                key={`${treatment.treatment_id}-${idx}`}
                variant="caption"
                color="text.secondary"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textAlign: 'left',
                }}
              >
                • {treatment.name}
              </Typography>
            ))}
            {selectedTreatments.length > 3 && (
              <Typography variant="caption" color="text.disabled">
                +{selectedTreatments.length - 3} more...
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CaseTreatmentsSummary;
